import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { isJWTValid, parseJWT } from '@/libs/auth'
import i18n, { type AvailableLocales } from '@/libs/i18n'

export interface LoggedInUserData {
    email: string
    ime:   string
}

export const useAppStore = defineStore('app', () => {
    const JWT         = ref(localStorage.getItem('JWT') || '')
    const locale      = ref<string>(i18n.global.locale.value)   // zacetniJezik iz libs/i18n
    const windowWidth = ref(window.innerWidth)
    const loaderActive = ref(0)

    // The signed-in user comes FROM the token — BI puts email / ime in its claims — so it is
    // there the moment the app starts, with no request and no empty user row while one is in flight.
    const userData = computed((): LoggedInUserData | undefined => {
        const claims = parseJWT(JWT.value)
        return claims ? { email: claims.email ?? '', ime: claims.ime ?? '' } : undefined
    })

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
    // never a second source of truth. Two modes only, 'light' | 'dark' — no System
    // option in the UI. Without `emitAuto` the ref always reads the RESOLVED mode, so a
    // first visit (nothing stored yet) shows whatever the OS prefers. See theme.md.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    // Keep the breakpoint LIVE: read once, `isMobile` would freeze the form factor (and
    // the mobile drawer) until a reload. Bound to the store's effect scope.
    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const getJWT     = () => JWT.value
    // A token that is merely PRESENT is not a session: an expired or malformed one must
    // count as logged out, or the guard lets the admin UI render and the first API call's
    // 401 throws the user to the login page a moment later — the flash.
    const isLoggedIn = () => isJWTValid(JWT.value)
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile   = () => windowWidth.value < 1024

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        i18n.global.locale.value = newLocale as AvailableLocales
    }

    return {
        JWT, locale, userData, loaderActive, colorMode,
        getJWT, isLoggedIn, isLoaderActive, isMobile,
        setLogin, setLogout, startLoading, stopLoading, changeLocale,
    }
})
